import { DiscoverCardPreview } from './discover-card-preview'
import {
  DiscoverContentField,
  DiscoverContentPicker,
} from './discover-content-picker'
import { SelectCreatorModal } from '../../components/modal/app_modals/selectCreatorModal'
import type { StrippedVendor } from '../../redux-toolkits/vendors/vendors.types'
import { useState, type FormEvent } from 'react'
import { toast } from 'react-toastify'
import { Button } from '../../components/button'
import { TextInput } from '../../components/inputs/textInput'
import { InterestSelector } from '../../components/inputs/interest-selector'
import { SelectProductModal } from '../../components/modal/app_modals/selectProductModal'
import { useGetCreatorVideosQuery } from '../../redux-toolkits/discover/discover-slice'
import {
  useGetDiscoverCardOptionsQuery,
  useGetDiscoverFeedCardPreviewQuery,
  useSaveDiscoverCardMutation,
  type AdminDiscoverCard,
  type CardProductSummary,
  type CardSourceOption,
  type DiscoverCardBody,
  type FeedCardType,
} from '../../redux-toolkits/discover/discover-card-slice'
import { useGetFulfilmentCentersQuery } from '../../redux-toolkits/vendors/vendors.slice'
import { getInterestErrorMessage } from '../../redux-toolkits/interests/interests.helpers'

export const cardTypes: { value: FeedCardType; label: string }[] = [
  { value: 'product', label: 'Product' },
  { value: 'creator_products', label: 'Creator products' },
  { value: 'product_video', label: 'Product video' },
  { value: 'essentials', label: 'Essentials list' },
  { value: 'article', label: 'Good List' },
  { value: 'event', label: 'Event at a centre' },
  { value: 'space', label: 'Experience centre' },
]
const blankCard: DiscoverCardBody = {
  title: '',
  type: 'product',
  sourceId: '',
  storefrontId: '',
  productIds: [],
  interestIds: [],
  status: 'draft',
}
function localDateTime(value?: string | null) {
  if (value === undefined || value === null) return ''
  const date = new Date(value)
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16)
}

export function DiscoverCardForm({
  card,
  onClose,
}: {
  card?: AdminDiscoverCard
  onClose: () => void
}) {
  const [form, setForm] = useState<DiscoverCardBody>(
    card === undefined
      ? blankCard
      : {
          title: card.title,
          type: card.type,
          sourceId: card.sourceId,
          storefrontId: card.storefrontId,
          productIds: card.productIds,
          interestIds: card.interestIds,
          status: card.status,
          videoId: card.videoId,
          centreId: card.centreId,
          startsAt: card.startsAt,
          endsAt: card.endsAt,
        },
  )
  const isProductCard = ['product', 'product_video', 'event'].includes(
    form.type,
  )
  const [contentPicker, setContentPicker] = useState<
    'source' | 'video' | 'centre'
  >()
  const [pickedSource, setPickedSource] = useState<CardSourceOption>()
  const [pickedCentre, setPickedCentre] = useState<CardSourceOption>()
  const [selectingCreator, setSelectingCreator] = useState(false)
  const [selectedCreators, setSelectedCreators] = useState<StrippedVendor[]>(
    () => {
      const creator = card?.preview?.creator
      if (card?.type !== 'creator_products' || !creator) return []
      return [
        {
          _id: creator._id,
          vendorId: creator._id,
          name: creator.name,
          logo: creator.logo ?? '',
          slug: '',
          banner: '',
          extendedBio: '',
        },
      ]
    },
  )
  const [selecting, setSelecting] = useState(false)
  const [productSummaries, setProductSummaries] = useState<
    CardProductSummary[]
  >(card?.productSummaries ?? [])
  const [saveCard, { isLoading: saving }] = useSaveDiscoverCardMutation()
  const { data: centresResponse, isError: centresError } =
    useGetFulfilmentCentersQuery({ page: 1, limit: 1000 })
  const centres = centresResponse?.data.docs ?? []
  const { currentData: selectedOptions = [] } = useGetDiscoverCardOptionsQuery(
    { type: form.type, selectedId: form.sourceId },
    { skip: form.sourceId === '' },
  )
  const source = pickedSource ?? selectedOptions[0]
  const creatorId = selectedOptions[0]?.vendor ?? ''
  const {
    currentData: videos = [],
    isFetching: videosLoading,
    isError: videosError,
    refetch: refetchVideos,
  } = useGetCreatorVideosQuery(creatorId, {
    skip: form.type !== 'product_video' || creatorId === '',
  })
  const linkedVideos = videos.filter(
    (video) => video.isActive && video.productIds.includes(form.sourceId),
  )
  const {
    currentData: preview,
    isFetching: previewLoading,
    isError: previewError,
  } = useGetDiscoverFeedCardPreviewQuery(
    {
      type: form.type,
      sourceId: form.sourceId,
      productIds: form.productIds,
      videoId: form.videoId,
      centreId: form.centreId,
    },
    { skip: form.sourceId === '' },
  )

  const sourceLabels: Record<FeedCardType, string> = {
    product: 'Product',
    product_video: 'Product',
    creator_products: 'Creator',
    essentials: 'Essentials list',
    article: 'Good List',
    event: 'Event',
    space: 'Experience centre',
  }
  const sourceSelection = form.sourceId
    ? source ?? {
        _id: form.sourceId,
        title: preview?.title ?? 'Current selection',
        banner: preview?.image,
      }
    : undefined
  const videoOptions = linkedVideos.map((video) => ({
    _id: video._id,
    title: video.title,
    images: [video.thumbnailUrl],
  }))
  const selectedVideo = videoOptions.find((video) => video._id === form.videoId)
  const selectedCentre =
    pickedCentre ?? centres.find((centre) => centre._id === form.centreId)

  const save = async (event: FormEvent) => {
    event.preventDefault()
    if (form.sourceId === '' || form.storefrontId === '') {
      toast.error('Select a storefront and content.')
      return
    }
    if (form.type === 'creator_products' && form.productIds.length < 2) {
      toast.error('Select at least two products from this creator.')
      return
    }
    if (form.type === 'product_video' && !form.videoId) {
      toast.error('Select a linked video.')
      return
    }
    if (form.type === 'event' && !form.centreId) {
      toast.error('Select the event location.')
      return
    }
    if (
      form.startsAt != null &&
      form.endsAt != null &&
      new Date(form.startsAt) >= new Date(form.endsAt)
    ) {
      toast.error('End time must be after start time.')
      return
    }
    try {
      await saveCard({ id: card?._id, body: form }).unwrap()
      toast.success('Feed card saved.')
      onClose()
    } catch (error) {
      toast.error(getInterestErrorMessage(error))
    }
  }
  return (
    <form
      onSubmit={(event) => {
        void save(event)
      }}
      className="space-y-5 rounded-xl border p-5"
    >
      <h3 className="text-lg font-semibold">
        {card === undefined ? 'Create feed card' : 'Edit feed card'}
      </h3>
      <div className="grid items-start gap-8 lg:grid-cols-2">
        <fieldset disabled={saving} className="space-y-5">
          <TextInput
            title="Card name (for admin)"
            hasIcon={false}
            value={form.title}
            maxLength={200}
            required
            onChange={(event) =>
              setForm({ ...form, title: event.target.value })
            }
          />
          <label className="flex flex-col gap-2">
            Storefront
            <select
              required
              className="rounded-lg border p-3"
              value={form.storefrontId}
              onChange={(event) =>
                setForm({ ...form, storefrontId: event.target.value })
              }
            >
              <option value="">Select storefront</option>
              {centres.map((centre) => (
                <option key={centre._id} value={centre._id}>
                  {centre.name}
                </option>
              ))}
            </select>
          </label>
          {centresError && <p role="alert">Unable to load storefronts.</p>}
          <label className="flex flex-col gap-2">
            Card type
            <select
              className="rounded-lg border p-3"
              value={form.type}
              onChange={(event) => {
                setForm({
                  ...form,
                  type: event.target.value as FeedCardType,
                  sourceId: '',
                  productIds: [],
                  videoId: null,
                  centreId: null,
                })
                setContentPicker(undefined)
                setPickedSource(undefined)
                setPickedCentre(undefined)
                setProductSummaries([])
                setSelectedCreators([])
              }}
            >
              {cardTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </label>
          <DiscoverContentField
            title={sourceLabels[form.type]}
            selection={sourceSelection}
            placeholder="Browse and search existing content."
            onOpen={() => {
              if (form.type === 'creator_products') {
                setSelectingCreator(true)
                return
              }
              if (isProductCard) {
                setSelecting(true)
                return
              }
              setContentPicker('source')
            }}
          />
          {form.type === 'creator_products' && (
            <section className="space-y-3">
              <p>Select 2–6 products from this creator.</p>
              {form.productIds.map((id) => (
                <div key={id} className="flex gap-3">
                  <span>
                    {productSummaries.find((product) => product._id === id)
                      ?.name ?? 'Selected product'}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setForm({
                        ...form,
                        productIds: form.productIds.filter(
                          (productId) => productId !== id,
                        ),
                      })
                    }
                  >
                    Remove
                  </button>
                </div>
              ))}
              <Button
                label="Select products"
                type="button"
                disabled={form.sourceId === '' || form.productIds.length >= 6}
                onClick={() => setSelecting(true)}
              />
            </section>
          )}
          {form.type === 'product_video' && (
            <div className="space-y-2">
              <DiscoverContentField
                title="Linked video"
                selection={selectedVideo}
                placeholder="Choose a video linked to this product."
                disabled={!form.sourceId || creatorId === ''}
                onOpen={() => setContentPicker('video')}
              />
              <p className="text-sm text-sec-black">
                Add videos and link products from the creator’s page first.
              </p>
            </div>
          )}
          {form.type === 'event' && (
            <DiscoverContentField
              title="Experience centre"
              selection={selectedCentre}
              placeholder="Choose the event location."
              onOpen={() => setContentPicker('centre')}
            />
          )}
          <div>
            <h4 className="font-medium mb-2">Interests</h4>
            <InterestSelector
              selectedIds={form.interestIds}
              onChange={(interestIds) => setForm({ ...form, interestIds })}
              description="Choose interests this card matches. Product and category matches are also used automatically."
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <TextInput
              title="Start showing (local time, optional)"
              hasIcon={false}
              type="datetime-local"
              value={localDateTime(form.startsAt)}
              onChange={(event) =>
                setForm({
                  ...form,
                  startsAt:
                    event.target.value === ''
                      ? null
                      : new Date(event.target.value).toISOString(),
                })
              }
            />
            <TextInput
              title="Stop showing (local time, optional)"
              hasIcon={false}
              type="datetime-local"
              value={localDateTime(form.endsAt)}
              onChange={(event) =>
                setForm({
                  ...form,
                  endsAt:
                    event.target.value === ''
                      ? null
                      : new Date(event.target.value).toISOString(),
                })
              }
            />
          </div>
          <label className="flex flex-col gap-2">
            Status
            <select
              className="rounded-lg border p-3"
              value={form.status}
              onChange={(event) =>
                setForm({
                  ...form,
                  status: event.target.value as DiscoverCardBody['status'],
                })
              }
            >
              <option value="draft">Draft / hidden</option>
              <option value="published">Published</option>
            </select>
          </label>
          <div className="flex gap-3">
            <Button
              label={saving ? 'Saving…' : 'Save card'}
              primary
              type="submit"
            />
            <Button label="Cancel" type="button" onClick={onClose} />
          </div>
        </fieldset>
        <aside className="mx-auto w-full max-w-sm space-y-3 lg:sticky lg:top-6">
          <h4 className="font-medium">Feed preview</h4>
          <DiscoverCardPreview
            key={`${form.type}:${form.sourceId}`}
            preview={preview}
            loading={previewLoading}
            error={previewError}
          />
          <p className="text-xs text-pry-gray">
            Preview of the card layout. Availability, likes, follow state and
            placement depend on the customer.
          </p>
        </aside>
      </div>
      {contentPicker === 'source' && (
        <DiscoverContentPicker
          type={form.type}
          title={sourceLabels[form.type]}
          selection={sourceSelection}
          onClose={() => setContentPicker(undefined)}
          onSelect={(option) => {
            setPickedSource(option)
            setForm({
              ...form,
              sourceId: option._id,
              productIds: [],
              videoId: null,
            })
            setContentPicker(undefined)
          }}
        />
      )}
      {contentPicker === 'video' && (
        <DiscoverContentPicker
          type="product_video"
          title="Linked video"
          selection={selectedVideo}
          options={videoOptions}
          loading={videosLoading}
          error={videosError}
          onRetry={() => {
            void refetchVideos()
          }}
          onClose={() => setContentPicker(undefined)}
          onSelect={(option) => {
            setForm({ ...form, videoId: option._id })
            setContentPicker(undefined)
          }}
        />
      )}
      {contentPicker === 'centre' && (
        <DiscoverContentPicker
          type="space"
          title="Experience centre"
          selection={selectedCentre}
          onClose={() => setContentPicker(undefined)}
          onSelect={(option) => {
            setPickedCentre(option)
            setForm({ ...form, centreId: option._id })
            setContentPicker(undefined)
          }}
        />
      )}
      <SelectCreatorModal
        open={selectingCreator}
        setOpen={setSelectingCreator}
        onCloseCallback={() => setSelectingCreator(false)}
        selectedCreators={selectedCreators}
        maxSelection={1}
        fulfillmentCenters={[]}
        onSuccessCallback={(creators) => {
          const creator = creators[0]
          if (!creator) return
          setSelectedCreators(creators)
          setPickedSource({
            _id: creator._id,
            name: creator.name,
            images: creator.logo ? [creator.logo] : [],
          })
          if (creator._id !== form.sourceId) {
            setForm({
              ...form,
              sourceId: creator._id,
              productIds: [],
              videoId: null,
            })
            setProductSummaries([])
          }
          setSelectingCreator(false)
        }}
      />
      <SelectProductModal
        open={selecting}
        setOpen={setSelecting}
        onCloseCallback={() => setSelecting(false)}
        selectedProducts={[]}
        excludedProducts={isProductCard ? [] : form.productIds}
        maxSelection={isProductCard ? 1 : 6 - form.productIds.length}
        query={{
          ...(form.type === 'creator_products' && { vendorId: form.sourceId }),
          ...(form.type === 'event' && { section: 'EVENT' }),
        }}
        onSuccessCallback={(selected) => {
          if (isProductCard) {
            const product = selected[0]
            if (!product) return
            setPickedSource({
              _id: product._id,
              name: product.name,
              images: product.images,
            })
            if (product._id !== form.sourceId) {
              setForm({
                ...form,
                sourceId: product._id,
                productIds: [],
                videoId: null,
              })
            }
            setSelecting(false)
            return
          }
          setForm({
            ...form,
            productIds: Array.from(
              new Set([
                ...form.productIds,
                ...selected.map((product) => product._id),
              ]),
            ).slice(0, 6),
          })
          setProductSummaries([
            ...productSummaries,
            ...selected.map((product) => ({
              _id: product._id,
              name: product.name,
            })),
          ])
          setSelecting(false)
        }}
      />
    </form>
  )
}
